/**
 * Threshold alerts — shared types + Zod schemas.
 *
 * The runtime lives in apps/api/src/alerts; this file is the wire
 * contract every consumer (API, MCP, future admin UI, embedded SDK)
 * agrees on. Stable across minor versions.
 */
import { z } from 'zod';
import { assertAllowedWebhookUrl, WebhookDestinationError } from './webhook-url-guard.js';
// ─── Channels ─────────────────────────────────────────────────────────
export const alertChannelEmailSchema = z.object({
    kind: z.literal('email'),
    /** Recipient list. Validated as RFC-shaped emails at write time. */
    to: z.array(z.string().email()).min(1).max(20),
    /** Optional subject override; falls back to a default. */
    subject: z.string().max(200).optional(),
});
/**
 * CSAGE-313: `url` must be `https:`, carry no embedded credentials, and
 * not be a loopback/link-local/private/reserved IP literal — checked
 * with the same syntactic guard `apps/api`'s webhook delivery channel
 * uses at write time (`assertAllowedWebhookUrl`; no allow-list here,
 * since there is no operator-configured exemption mechanism for alerts).
 * This is write-time-only: there is no `apps/api/src/alerts` runtime
 * today, so there is no send-time guard to pair it with and no stored
 * row this could ever break by tightening in place.
 */
const alertWebhookUrlSchema = z.string().superRefine((url, ctx) => {
    try {
        assertAllowedWebhookUrl(url, { allowedHosts: [] });
    }
    catch (err) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: err instanceof WebhookDestinationError
                ? err.message
                : 'Webhook URL must be https:// and resolve to a public destination.',
        });
    }
});
export const alertChannelWebhookSchema = z.object({
    kind: z.literal('webhook'),
    /** Fully-qualified `https:` URL; rejected at write time if it's a
     *  loopback/link-local/private/reserved literal (CSAGE-313). */
    url: alertWebhookUrlSchema,
    /** Optional shared secret used in an HMAC-SHA256 signature header
     *  the receiver verifies. */
    hmacSecret: z.string().min(8).max(200).optional(),
    /** Optional headers, e.g. `Authorization: Bearer …`. */
    headers: z.record(z.string(), z.string()).optional(),
});
export const alertChannelSchema = z.discriminatedUnion('kind', [
    alertChannelEmailSchema,
    alertChannelWebhookSchema,
]);
// ─── Comparator ──────────────────────────────────────────────────────
export const alertComparatorSchema = z.enum(['>', '>=', '<', '<=', '=', '!=']);
// ─── Rule ────────────────────────────────────────────────────────────
export const alertRuleInputSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    datasetId: z.string().uuid(),
    /** Must match a measure name declared in the dataset's semantic
     *  block (validated at write time). */
    measureName: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/u),
    filterValues: z.record(z.string(), z.unknown()).optional(),
    comparator: alertComparatorSchema,
    threshold: z.number().finite(),
    /** Standard cron expression in 5-field form. */
    cron: z.string().min(1).max(120),
    cronTimezone: z.string().min(1).max(60).default('UTC'),
    channels: z.array(alertChannelSchema).min(1).max(8),
    fireOncePerBreach: z.boolean().default(true),
    enabled: z.boolean().default(true),
});
/**
 * Pure helper: apply the comparator. Centralized so the evaluator,
 * API, and tests all agree exactly when a rule fires.
 */
export function evaluateThreshold(observed, comparator, threshold) {
    switch (comparator) {
        case '>':
            return observed > threshold;
        case '>=':
            return observed >= threshold;
        case '<':
            return observed < threshold;
        case '<=':
            return observed <= threshold;
        case '=':
            return observed === threshold;
        case '!=':
            return observed !== threshold;
    }
}
//# sourceMappingURL=alerts.js.map